import { kebabCase } from "@/Utils/formatters";
import { Flex, Select, Input, Text } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import {
  MAPPING_TEST_PREFIX,
  ValidMappedFieldCheck
} from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/Utils";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { CustomMappingPopover } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/CustomMappingPopover";
import { PositionConfig } from "@coreapp/CoreApp/PositionConfig";
import * as React from "react";
import { enumObjectTypes } from "@coreapp/constants";

export const CostBandFieldMappingRow = ({
  fieldKey,
  fieldValue,
  fieldType,
  localConfig,
  BuildFieldMappingOptions,
  handleFieldRename,
  handleCustomFieldNameValidation,
  disabled = false,
  tooltipProps = undefined,
  onUpdate
}) => {
  const posConfig = new PositionConfig(localConfig);

  const isSystem = posConfig.isFieldUsingSystemCalculation(fieldKey);
  const isEmployee = fieldType === enumObjectTypes.holder;

  const initialPercentage = isSystem ? posConfig.getSystemCalculationPercentage(fieldKey) : "";
  const [percentage, setPercentage] = React.useState(initialPercentage);

  const [tempPercentage, setTempPercentage] = React.useState(initialPercentage);

  React.useEffect(() => {
    if (isSystem) {
      const newPercentage = posConfig.getSystemCalculationPercentage(fieldKey);
      setPercentage(newPercentage);
      setTempPercentage(newPercentage);
    }
  }, [isSystem, fieldKey, localConfig.positionChart?.costBandSystem]);

  const getFieldOptions = () => {
    const standardOptions = BuildFieldMappingOptions(fieldKey);

    const disableMidpointSystem =
      fieldKey === "Position Cost Band Midpoint" &&
      (posConfig.isFieldUsingSystemCalculation("Position Cost Band Minimum") ||
        posConfig.isFieldUsingSystemCalculation("Position Cost Band Maximum"));

    const disableMinMaxSystem =
      (fieldKey === "Position Cost Band Minimum" || fieldKey === "Position Cost Band Maximum") &&
      posConfig.isFieldUsingSystemCalculation("Position Cost Band Midpoint");

    return [
      {
        label: appstr("System calculate for me"),
        value: "system",
        disabled:
          (fieldKey === "Position Cost Band Midpoint" && disableMidpointSystem) ||
          ((fieldKey === "Position Cost Band Minimum" ||
            fieldKey === "Position Cost Band Maximum") &&
            disableMinMaxSystem),
        clickHandler: e => handleFieldMappingChange("system", fieldKey)
      },
      ...standardOptions
    ];
  };

  const handleFieldMappingChange = (value, fieldMapID) => {
    if (value === "system") {
      const defaultPercentage = posConfig.getDefaultPercentage(fieldMapID);
      setPercentage(defaultPercentage);
      setTempPercentage(defaultPercentage);

      const currentSystemSettings = localConfig.positionChart?.costBandSystem || {};
      const updatedSystemSettings = {
        ...currentSystemSettings,
        [fieldMapID]: defaultPercentage
      };

      onUpdate(
        {
          fields: { ...localConfig.fields, [fieldMapID]: PositionConfig.SYSTEM_PREFIX },
          positionChart: {
            ...localConfig.positionChart,
            costBandSystem: updatedSystemSettings
          }
        },
        ["fields", "positionChart"]
      );
    } else {
      const currentSystemSettings = localConfig.positionChart?.costBandSystem || {};
      const updatedSystemSettings = { ...currentSystemSettings };
      delete updatedSystemSettings[fieldMapID];

      onUpdate(
        {
          fields: { ...localConfig.fields, [fieldMapID]: value },
          positionChart: {
            ...localConfig.positionChart,
            costBandSystem: updatedSystemSettings
          }
        },
        ["fields", "positionChart"]
      );
    }
  };

  const handlePercentageInputChange = e => {
    const newValue = e.target.value;
    if (newValue === "" || /^\d{0,3}$/.test(newValue)) {
      setTempPercentage(newValue);
    }
  };

  const getMinMaxValues = fieldKey => {
    if (fieldKey === "Position Cost Band Minimum") {
      return { min: 0, max: 100 };
    } else if (fieldKey === "Position Cost Band Maximum") {
      return { min: 100, max: 999 };
    }
    return { min: 0, max: 999 };
  };

  const handlePercentageBlur = () => {
    if (tempPercentage === "") {
      setTempPercentage(percentage);
      return;
    }

    const numValue = parseInt(tempPercentage, 10);
    const { min, max } = getMinMaxValues(fieldKey);

    if (numValue >= min && numValue <= max) {
      setPercentage(tempPercentage);

      const currentSystemSettings = localConfig.positionChart?.costBandSystem || {};
      const updatedSystemSettings = {
        ...currentSystemSettings,
        [fieldKey]: tempPercentage
      };

      onUpdate(
        {
          positionChart: {
            ...localConfig.positionChart,
            costBandSystem: updatedSystemSettings
          }
        },
        ["positionChart"]
      );
    } else {
      setTempPercentage(percentage);
    }
  };

  const getExplanationText = () => {
    if (fieldKey === "Position Cost Band Minimum") {
      return "[cost band midpoint]  x  ";
    } else if (fieldKey === "Position Cost Band Midpoint") {
      return "average of cost band minimum and maximum";
    } else if (fieldKey === "Position Cost Band Maximum") {
      return "[cost band midpoint]  x  ";
    }
    return "";
  };

  const { min, max } = getMinMaxValues(fieldKey);

  return (
    <Flex
      justifyContent={"space-between"}
      alignItems={"end"}
      key={`${fieldValue}-${fieldKey}`}
      className={"oc-cost-band-select-wrapper"}
    >
      <div style={{ width: "50%", flexShrink: 0, flexGrow: 0 }}>
        <div className="oc-cost-band-select-container" style={{ width: "100%" }}>
          <Select
            dataTestId={`${MAPPING_TEST_PREFIX}-costband-select-${kebabCase(fieldKey)}`}
            inputOnly={false}
            label={appstr(fieldKey)}
            message={ValidMappedFieldCheck(localConfig, { [fieldKey]: fieldValue }, fieldKey)}
            placeholder={
              fieldValue
                ? isSystem
                  ? "System calculate for me"
                  : fieldValue
                : appstr("Select field")
            }
            selectSize={"medium"}
            optionSize={"medium"}
            listWidth="fill"
            listProps={{ ...FieldSelectListProps(), enableSearch: false }}
            value={isSystem ? "system" : fieldValue}
            options={getFieldOptions()}
            disabled={disabled}
            require={true}
            tooltipProps={tooltipProps}
            className={"oc-cost-band-select"}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      <Flex
        alignItems="center"
        gap="8px"
        style={{
          flexGrow: 1,
          marginLeft: "8px",
          justifyContent: "flex-start",
          visibility: isSystem ? "visible" : "hidden"
        }}
      >
        <Text size="small" color="secondary">
          {isSystem ? getExplanationText() : ""}
        </Text>
        {isSystem &&
          (fieldKey === "Position Cost Band Minimum" ||
            fieldKey === "Position Cost Band Maximum") && (
            <Flex alignItems="center" gap="4px">
              <Input
                dataTestId={`${MAPPING_TEST_PREFIX}-costband-pct-input-${kebabCase(fieldKey)}`}
                type="number"
                value={tempPercentage}
                onChange={handlePercentageInputChange}
                onBlur={handlePercentageBlur}
                disabled={disabled}
                size={"medium"}
                min={min}
                max={max}
                style={{ width: "60px" }}
                maxLength={3}
                inputMode="numeric"
                pattern="\d{0,3}"
              />
              <Text size="small">%</Text>
            </Flex>
          )}
      </Flex>
      <CustomMappingPopover
        onUpdate={handleFieldRename}
        fieldID={fieldKey}
        currentField={localConfig["fields"][fieldKey]}
        validateFxn={handleCustomFieldNameValidation}
        disabled={disabled || isSystem || isEmployee}
      />
    </Flex>
  );
};
